import React from 'react';
import { ArrowLeft, List, Map } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../../../components/ui/dialog';
import { Button } from '../../../../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../../components/ui/tabs';
import type { SpoolCard, WeldJoint } from '../../SpoolsProduction';
import { weldJointStatusCell } from './weldStatusBadge';
import { WeldingPassSampleTable } from './WeldingPassSampleTable';
import { WeldingZoneSampleTable } from './WeldingZoneSampleTable';

export interface WeldTelemetryModalProps {
  open: boolean;
  onClose: () => void;
  spool: SpoolCard;
  weld: WeldJoint | null;
}

const tabTriggerClass =
  'data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap';

/** Full-screen weld telemetry: Pass / Zone sample tables (tab strip matches {@link WeldPassList}). */
export function WeldTelemetryModal({ open, onClose, spool, weld }: WeldTelemetryModalProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="m-0 h-screen max-h-none w-screen max-w-none gap-0 overflow-hidden rounded-none border-0 bg-white p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col">
          <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-4 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={onClose} aria-label="Back">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="min-w-0 space-y-1">
                <DialogTitle className="text-left text-lg font-bold text-gray-900">Weld data</DialogTitle>
                <p className="truncate text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Spool:</span> {spool.name}
                </p>
                {weld && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
                    <span>
                      <span className="text-gray-500">Weld:</span>{' '}
                      <span className="font-mono font-semibold text-gray-900">{weld.number}</span>
                    </span>
                    <span className="hidden text-gray-300 sm:inline">|</span>
                    <span>
                      <span className="text-gray-500">Welder:</span> {weld.welder}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-gray-500">Status:</span>
                      {weldJointStatusCell(weld)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <section className="flex min-h-0 flex-1 flex-col">
              <Tabs defaultValue="pass" className="flex h-full min-h-0 w-full flex-col">
                <div className="sticky top-0 z-10 flex-shrink-0 rounded-t-lg border-b border-gray-200 bg-white">
                  <div className="flex items-center px-1 py-2 md:px-4">
                    <TabsList className="h-auto flex-1 flex-wrap justify-start gap-2 border-b bg-transparent py-2 md:gap-4 lg:gap-8">
                      <TabsTrigger value="pass" className={tabTriggerClass}>
                        <List className="mr-1 inline-block h-4 w-4 align-text-bottom" />
                        Pass view
                      </TabsTrigger>
                      <TabsTrigger value="zone" className={tabTriggerClass}>
                        <Map className="mr-1 inline-block h-4 w-4 align-text-bottom" />
                        Zone view
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </div>
                <TabsContent
                  value="pass"
               //   className="m-0 mt-0 min-h-0 flex-1 p-0 pt-3 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:flex data-[state=inactive]:hidden"
                >
                  <WeldingPassSampleTable embedded titleHint={spool.name} />
                </TabsContent>
                <TabsContent
                  value="zone"
                  //className="m-0 mt-0 min-h-0 flex-1 p-0 pt-3 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:flex data-[state=inactive]:hidden"
                >
                  <WeldingZoneSampleTable embedded titleHint={spool.name} />
                </TabsContent>
              </Tabs>
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
